import { PlayerPortalGetPlayerCompetitionsResponse } from "@mixitone/actions";
import { withStateToggle } from "@mixitone/util";
import { PortalController } from "controllers/PortalController";
import portalFunctions from "../../../lib/portalFunctions";

export type RegistrationResponse = PlayerPortalGetPlayerCompetitionsResponse["registrations"][number];

/** Grace period before hiding tournaments that never reached active/completed. */
const NEVER_RAN_HIDE_AFTER_MS = 48 * 60 * 60 * 1000;

const RAN_STATUSES = new Set(["active", "completed"]);

function tournamentActuallyRan(reg: RegistrationResponse): boolean {
  if (reg.session?.completed_at) return true;
  if (RAN_STATUSES.has(reg.session?.status)) return true;
  if (RAN_STATUSES.has(reg.competition?.status)) return true;
  return false;
}

/** Never-run tournaments drop off upcoming once end date + 48h has passed. */
function shouldHideNeverRanTournament(reg: RegistrationResponse, now: Date): boolean {
  if (tournamentActuallyRan(reg)) return false;
  const endsAt = reg.session?.ends_at;
  if (!endsAt) return false;
  return now.getTime() > new Date(endsAt).getTime() + NEVER_RAN_HIDE_AFTER_MS;
}

function isCompletedRegistration(reg: RegistrationResponse): boolean {
  return (
    !!reg.session?.completed_at ||
    reg.competition?.status === "completed" ||
    reg.session?.status === "completed"
  );
}

interface State {
  loading: boolean;
  currentAndFutureRegistrations: RegistrationResponse[];
  pastRegistrations: RegistrationResponse[];
  error: string | null;
  sortBy: "date" | "competition" | "placement";
  filterBy: "all" | "singles" | "doubles";
}

export class PlayerCompetitionsPageController extends PortalController<State> {
  get initialState(): State {
    return {
      loading: true,
      currentAndFutureRegistrations: [],
      pastRegistrations: [],
      error: null,
      sortBy: "date",
      filterBy: "all",
    };
  }

  async initialize() {
    await this.loadPlayerCompetitions();
  }

  @withStateToggle("loading")
  private async loadPlayerCompetitions() {
    try {
      // Use edge function to bypass RLS and get accurate partner status
      const response = await portalFunctions.getPlayerCompetitions({});

      if (!response.registrations || response.registrations.length === 0) {
        return;
      }

      const registrations = response.registrations;
      const now = new Date();

      // Hide tournaments that never ran once 48h past the scheduled end
      const visible = registrations.filter((reg) => !shouldHideNeverRanTournament(reg, now));

      const pastRegistrations = visible.filter((reg) => {
        if (isCompletedRegistration(reg)) return true;
        if (!tournamentActuallyRan(reg)) return false;
        const endsAt = reg.session?.ends_at;
        return !!endsAt && new Date(endsAt) < now;
      });

      const currentAndFuture = visible.filter((reg) => {
        if (isCompletedRegistration(reg)) return false;
        const endsAt = reg.session?.ends_at;
        // Ran and end date passed → past only
        if (tournamentActuallyRan(reg) && endsAt && new Date(endsAt) < now) return false;
        // Never-ran stays upcoming until the 48h hide cutoff (already applied above)
        return true;
      });

      // Create registration with placement objects, using computed status from edge function
      // Apply sorting and filtering to past registrations
      this.state.pastRegistrations = this.applySortingAndFiltering(pastRegistrations);
      this.state.currentAndFutureRegistrations = this.applySortingAndFiltering(currentAndFuture);
    } catch (error) {
      console.error("Error loading player competitions:", error);
      this.state.error = "Error loading tournament competitions";
    }
  }

  actionClearError() {
    this.state.error = null;
  }

  actionSetSortBy(sortBy: "date" | "competition" | "placement") {
    this.state.sortBy = sortBy;
    this.applySortingAndFiltering();
  }

  actionSetFilterBy(filterBy: "all" | "singles" | "doubles") {
    this.state.filterBy = filterBy;
    this.applySortingAndFiltering();
  }

  private applySortingAndFiltering(registrations?: RegistrationResponse[]): RegistrationResponse[] {
    const registrationsToSort = registrations || this.state.pastRegistrations;

    // Apply filter
    let filtered = registrationsToSort;
    if (this.state.filterBy === "singles") {
      filtered = filtered.filter((item) => item.competition.entry_type === "singles");
    } else if (this.state.filterBy === "doubles") {
      filtered = filtered.filter((item) => item.competition.entry_type === "doubles");
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      if (this.state.sortBy === "date") {
        const dateA = a.session.completed_at || a.session.ends_at || "";
        const dateB = b.session.completed_at || b.session.ends_at || "";
        return dateB.localeCompare(dateA); // Most recent first
      } else if (this.state.sortBy === "competition") {
        const nameA = a.competition.name || "";
        const nameB = b.competition.name || "";
        return nameA.localeCompare(nameB);
      } else if (this.state.sortBy === "placement") {
        // Sort by placement (lower is better), nulls last
        if (a.placement === null && b.placement === null) return 0;
        if (a.placement === null) return 1;
        if (b.placement === null) return -1;
        return a.placement - b.placement;
      }
      return 0;
    });

    return sorted;
  }
}
